import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";

// --- Defesa contra crash de DOM por tradução automática / extensões -------
// Navegadores (Google Translate, WebViews, extensões) reescrevem nós de texto
// por fora do React. Quando o React desmonta/reconcilia, chama removeChild/
// insertBefore num nó que já não está onde ele espera → NotFoundError.
// Em vez de quebrar a árvore inteira, ignoramos a operação inválida.
// Ref: https://github.com/facebook/react/issues/11538
if (typeof Node === "function" && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      if (import.meta.env.DEV) {
        console.warn("removeChild: nó não é filho deste pai — ignorando", { child, parent: this });
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (import.meta.env.DEV) {
        console.warn("insertBefore: nó de referência não é filho deste pai — ignorando", { referenceNode, parent: this });
      }
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
// --------------------------------------------------------------------------
import posthog from "posthog-js";
import App from "./App";
import "./index.css";

// Initialize Sentry (only if DSN is configured)
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    tracePropagationTargets: [
      /^\//,
      /^https:\/\/rizoma\.farm/,
    ],
  });
}

// Initialize PostHog (only if key is configured)
if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com",
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    person_profiles: "identified_only",
  });
}

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary
    fallback={({ resetError }) => (
      <div style={{ padding: 24, textAlign: "center", fontFamily: "Inter, system-ui, sans-serif" }}>
        <p style={{ marginBottom: 12 }}>Algo deu errado ao carregar esta tela.</p>
        <button
          onClick={resetError}
          style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #ccc", cursor: "pointer" }}
        >
          Tentar novamente
        </button>
      </div>
    )}
  >
    <App />
  </Sentry.ErrorBoundary>,
);
